import { BaseModel } from "@/models/base.model"
import { type IProductionStatement } from "@/interfaces/api/production-statement.interface"
import { Project } from "@/models/project.model"
import { ModelFactory } from "@/factories/model-factory"
import { type Moment } from "moment"
import { Sale } from "@/models/sale.model"
import { RentalDispense } from "@/models/rental-dispense.model"
import { RentalDispenseMaterialization } from "@/models/rental-dispense-materialization.model"
import type { DraftProductionStatement } from "@/models/draft-production-statement.model"
import { FileBucket } from "@/models/file-bucket.model"

export class ProductionStatement extends BaseModel implements IProductionStatement {
  public static className = "ProductionStatement" // Used for socket communication.
  public static verboseName = "productieverantwoording"
  public static override managedResolveFields = ["project"]

  project: Project
  fileBucket?: FileBucket
  sales: Sale[]
  rentalDispenseMaterializations: RentalDispenseMaterialization[]
  rentalDispenses: RentalDispense[]

  periodStart?: Moment
  periodEnd?: Moment
  reference?: string
  disputedAt?: Moment
  disputeReason?: string
  lastSentAt?: Moment
  sentAtHistory: Moment[]

  // Annotated fields
  salesSum: string
  rentalDispenseMaterializationsSum: string
  saleIds: string[]
  rentalDispenseIds: string[]
  rentalDispenseMaterializationIds: string[]

  constructor(productionStatement: IProductionStatement, managed = false) {
    super(productionStatement)

    this.project = ModelFactory.childModel(Project, productionStatement.project, managed)
    this.fileBucket = productionStatement.fileBucket
      ? ModelFactory.childModel(FileBucket, productionStatement.fileBucket, managed)
      : undefined
    this.sales = ModelFactory.childModels(Sale, productionStatement.sales, managed)
    this.rentalDispenseMaterializations = ModelFactory.childModels(
      RentalDispenseMaterialization,
      productionStatement.rentalDispenseMaterializations,
      managed
    )
    this.rentalDispenses = ModelFactory.childModels(
      RentalDispense,
      productionStatement.rentalDispenses,
      managed
    )

    this.periodStart = productionStatement.periodStart
    this.periodEnd = productionStatement.periodEnd
    this.reference = productionStatement.reference
    this.disputedAt = productionStatement.disputedAt
    this.disputeReason = productionStatement.disputeReason
    this.lastSentAt = productionStatement.lastSentAt
    this.sentAtHistory = productionStatement.sentAtHistory ?? []

    this.salesSum = productionStatement.salesSum ?? "0"
    this.rentalDispenseMaterializationsSum =
      productionStatement.rentalDispenseMaterializationsSum ?? "0"
    this.saleIds = productionStatement.saleIds ?? []
    this.rentalDispenseIds = productionStatement.rentalDispenseIds ?? []
    this.rentalDispenseMaterializationIds =
      productionStatement.rentalDispenseMaterializationIds ?? []
  }

  /** The total sum of sales and rental dispense materializations of the production statement. */
  public get sum() {
    return parseFloat(this.salesSum) + parseFloat(this.rentalDispenseMaterializationsSum)
  }

  public getDisplayText(): string {
    return this.reference ? `productieverantwoording ${this.reference}` : "productieverantwoording"
  }

  public isDraft(): this is DraftProductionStatement {
    return false
  }

  /** The ISO week numbers of the production statement. */
  public get displayPeriodISOWeek(): string {
    const startWeek = this.periodStart?.isoWeek()
    const endWeek = this.periodEnd?.isoWeek()

    if (startWeek === undefined) return endWeek === undefined ? "" : `wk ${endWeek}`
    if (endWeek === undefined || endWeek === startWeek) return `wk ${startWeek}`

    return `wk ${startWeek} t/m wk ${endWeek}`
  }
}
